/**
 * Artisan Pad Finder Custom Element
 * Interactive product recommendation based on speed and control preferences
 */

if (!customElements.get('artisan-pad-finder')) {
  customElements.define(
    'artisan-pad-finder',
    class ArtisanPadFinder extends HTMLElement {
      constructor() {
        super();

        // Parse products data from data attribute
        try {
          this.productsData = JSON.parse(this.dataset.products || '[]');
        } catch (e) {
          console.error('Failed to parse products data:', e);
          this.productsData = [];
        }

        // Get slider elements
        this.speedSlider = this.querySelector('[name="speed-slider"]');
        this.controlSlider = this.querySelector('[name="control-slider"]');
        this.resultsContainer = this.querySelector('.finder-results');

        // Get value display elements
        this.speedValueDisplay = this.querySelector('[data-slider="speed"]');
        this.controlValueDisplay = this.querySelector('[data-slider="control"]');

        if (!this.speedSlider || !this.controlSlider || !this.resultsContainer) {
          console.error('Required elements not found in artisan-pad-finder');
          return;
        }

        // Bind event listeners
        this.speedSlider.addEventListener('input', this.handleSliderInput.bind(this));
        this.controlSlider.addEventListener('input', this.handleSliderInput.bind(this));

        // Initial render with default values
        this.updateRecommendations();
      }

      /**
       * Handle slider input events
       */
      handleSliderInput(event) {
        const slider = event.target;
        const value = parseInt(slider.value);

        // Update value display
        if (slider.name === 'speed-slider' && this.speedValueDisplay) {
          this.speedValueDisplay.textContent = value;
        } else if (slider.name === 'control-slider' && this.controlValueDisplay) {
          this.controlValueDisplay.textContent = value;
        }

        // Update recommendations
        this.updateRecommendations();
      }

      /**
       * Calculate and render product recommendations
       */
      updateRecommendations() {
        const speedPref = parseInt(this.speedSlider.value);
        const controlPref = parseInt(this.controlSlider.value);

        // Calculate recommendations
        const recommendations = this.calculateRecommendations(speedPref, controlPref);

        // Render results
        this.renderResults(recommendations);
      }

      /**
       * Calculate product scores based on user preferences
       * Uses Euclidean distance to find closest matches
       */
      calculateRecommendations(speedPref, controlPref) {
        if (!this.productsData || this.productsData.length === 0) {
          return [];
        }

        return this.productsData
          .map((product) => ({
            ...product,
            score: this.calculateScore(product, speedPref, controlPref),
          }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 2); // Top 2 recommendations
      }

      /**
       * Calculate match score using inverse Euclidean distance
       * Higher score = better match
       */
      calculateScore(product, speedPref, controlPref) {
        const speedDiff = Math.abs(product.speed - speedPref);
        const controlDiff = Math.abs(product.control - controlPref);
        const distance = Math.sqrt(speedDiff ** 2 + controlDiff ** 2);

        // Convert distance to score (10 = perfect match, 0 = worst match)
        // Max possible distance is sqrt(10^2 + 10^2) = ~14.14
        return Math.max(0, 10 - (distance / 14.14) * 10);
      }

      /**
       * Render recommendation cards
       */
      renderResults(recommendations) {
        if (!recommendations || recommendations.length === 0) {
          this.resultsContainer.innerHTML = `
            <div class="finder-results__empty">
              <p class="artisan-text-muted">No se encontraron productos. Por favor configura la colección en el Theme Editor.</p>
            </div>
          `;
          return;
        }

        const resultsHTML = recommendations
          .map((product, index) => this.createResultCard(product, index))
          .join('');

        this.resultsContainer.innerHTML = `
          <div class="finder-results__grid">
            ${resultsHTML}
          </div>
        `;
      }

      /**
       * Create HTML for a single result card
       */
      createResultCard(product, index) {
        const matchScore = Math.round(product.score * 10);
        const animationDelay = index * 100;

        return `
          <div class="result-card" style="--animation-delay: ${animationDelay}ms">
            ${
              product.image
                ? `
              <div class="result-card__image">
                <img src="${this.escapeHtml(product.image)}" alt="${this.escapeHtml(product.title)}" loading="lazy">
              </div>
            `
                : ''
            }
            <div class="result-card__content">
              <div class="result-card__header">
                <h3 class="result-card__title">${this.escapeHtml(product.title)}</h3>
                <div class="result-card__match">
                  <span class="result-card__match-label">Match:</span>
                  <span class="result-card__match-score">${matchScore}%</span>
                </div>
              </div>

              ${
                product.description
                  ? `<p class="result-card__description">${this.escapeHtml(product.description)}</p>`
                  : ''
              }

              <div class="result-card__specs">
                <div class="spec">
                  <span class="spec__label">Velocidad</span>
                  <div class="spec__bar-container">
                    <div class="spec__bar" style="--value: ${product.speed * 10}%"></div>
                  </div>
                  <span class="spec__value">${product.speed}/10</span>
                </div>
                <div class="spec">
                  <span class="spec__label">Control</span>
                  <div class="spec__bar-container">
                    <div class="spec__bar" style="--value: ${product.control * 10}%"></div>
                  </div>
                  <span class="spec__value">${product.control}/10</span>
                </div>
              </div>

              ${
                product.surfaceType
                  ? `<div class="result-card__surface-type">
                <span class="surface-type-badge">${this.escapeHtml(product.surfaceType)}</span>
              </div>`
                  : ''
              }

              <a href="${this.escapeHtml(product.url)}" class="result-card__button artisan-button artisan-button-primary">
                Ver ${this.escapeHtml(product.title)}
              </a>
            </div>
          </div>
        `;
      }

      /**
       * Escape HTML to prevent XSS
       */
      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
    }
  );
}
